import { useEffect, useState } from "react";
import { Button, Empty, Input, message, Select, Space, Spin } from "antd";
import {
    LeftOutlined,
    NumberOutlined,
    ReloadOutlined,
    RightOutlined,
    VerticalAlignTopOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DatePicker from "../../../core/components/DatePicker";
import ApiService from "../../../core/service/ApiService";
import { matchesSearch } from "../../../core/utils/text-search";
import "./SystemLogViewer.css";

const LEVELS = {
    Verbose: { code: 'VRB', color: '#8c8c8c' },
    Debug: { code: 'DBG', color: '#8c8c8c' },
    Information: { code: 'INF', color: '#1890ff' },
    Warning: { code: 'WRN', color: '#faad14' },
    Error: { code: 'ERR', color: '#f5222d' },
    Fatal: { code: 'FTL', color: '#a8071a' },
};

// Bilinen kanallar için fallback (dinamik liste boşsa). Sunucu system_log_dimension'dan gerçek listeyi döndürür.
const CHANNELS = ['App', 'PatientResult', 'Worker', 'Auth', 'Http', 'Worklist'];

// Varsayılan tarih aralığı: son 7 gün. Sınırsız sorgu hem yavaş hem de yöneticinin aradığı şey değil.
const defaultDateRange = () => [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')];

// HTTP durum kodu filtresi: ya tam kod (statusCode) ya da alt sınır (statusCodeMin).
const STATUS_FILTERS = [
    { value: 'err', label: '4xx + 5xx', filter: { statusCode: null, statusCodeMin: 400 } },
    { value: 'srv', label: '5xx', filter: { statusCode: null, statusCodeMin: 500 } },
    { value: '200', label: '200', filter: { statusCode: 200, statusCodeMin: null } },
    { value: '401', label: '401', filter: { statusCode: 401, statusCodeMin: null } },
    { value: '403', label: '403', filter: { statusCode: 403, statusCodeMin: null } },
    { value: '404', label: '404', filter: { statusCode: 404, statusCodeMin: null } },
    { value: '500', label: '500', filter: { statusCode: 500, statusCodeMin: null } },
    { value: '503', label: '503', filter: { statusCode: 503, statusCodeMin: null } },
];

const statusFilterOf = (value) =>
    STATUS_FILTERS.find(s => s.value === value)?.filter ?? { statusCode: null, statusCodeMin: null };

// Logun kahramanı (aktör) tipleri — LogFields.Actor ile aynı küme.
const ACTOR_TYPES = {
    User: { code: 'USR', color: '#1890ff' },
    Patient: { code: 'HST', color: '#13c2c2' },
    AppClient: { code: 'CLI', color: '#722ed1' },
    Worker: { code: 'JOB', color: '#fa8c16' },
    Anonymous: { code: 'ANON', color: '#8c8c8c' },
    System: { code: 'SYS', color: '#fa8c16' },
};

function formatTime(ts) {
    const d = new Date(ts);
    const pad = (n, l = 2) => String(n).padStart(l, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function shortSource(source) {
    if (!source) return '';
    const parts = source.split('.');
    return parts[parts.length - 1];
}

function prettyJson(jsonStr) {
    try {
        return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch {
        return jsonStr;
    }
}

export default function SystemLogViewer() {
    const { t } = useTranslation();

    const [levels, setLevels] = useState([]);
    const [search, setSearch] = useState('');
    const [sourceContext, setSourceContext] = useState('');
    const [logSource, setLogSource] = useState(undefined);
    const [traceId, setTraceId] = useState('');
    const [actorType, setActorType] = useState(undefined);
    const [actorName, setActorName] = useState('');
    const [ip, setIp] = useState('');
    const [route, setRoute] = useState(undefined);
    const [status, setStatus] = useState(undefined);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [pageSize, setPageSize] = useState(100);

    const [items, setItems] = useState([]);
    const [hasPrev, setHasPrev] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [ascending, setAscending] = useState(false);
    const [pageIndex, setPageIndex] = useState(1);
    const [loading, setLoading] = useState(false);
    const [expandedIds, setExpandedIds] = useState(new Set());

    const [exactCount, setExactCount] = useState(null);
    const [countLoading, setCountLoading] = useState(false);

    const [sourceOptions, setSourceOptions] = useState([]);
    const [channelOptions, setChannelOptions] = useState([]);
    const [routeOptions, setRouteOptions] = useState([]);

    const buildFilter = (overrides = {}) => ({
        levels: levels.length > 0 ? levels : null,
        search: search || null,
        sourceContext: sourceContext || null,
        logSource: logSource || null,
        traceId: traceId || null,
        actorType: actorType || null,
        actorName: actorName || null,
        ip: ip || null,
        route: route || null,
        ...statusFilterOf(status),
        dateFrom: dateRange?.[0]?.toISOString() ?? null,
        dateTo: dateRange?.[1]?.toISOString() ?? null,
        ...overrides,
    });

    const fetchPage = async ({ prev, next, reset, filterOverride, ascendingOverride } = {}) => {
        setLoading(true);
        const asc = ascendingOverride ?? ascending;
        const response = await ApiService.SystemLog.GetSystemLogPage({
            filter: buildFilter(filterOverride),
            pageSize,
            ascending: asc,
            prev,
            next
        });
        setLoading(false);

        if (!response.success)
            return;

        const data = response.data;

        // Sınıra gelindi: sayfa boş döndüyse mevcut görünümü koru, yönü kapat.
        if ((prev || next) && (!data.items || data.items.length === 0)) {
            if (next) setHasNext(false);
            if (prev) setHasPrev(false);
            return;
        }

        setItems(data.items ?? []);
        setHasPrev(data.hasPrev);
        setHasNext(data.hasNext);
        setExpandedIds(new Set());
        setPageIndex(p => reset ? 1 : (next ? p + 1 : Math.max(1, p - 1)));
    };

    const goStart = () => fetchPage({ reset: true });

    const goNext = () => {
        const last = items[items.length - 1];
        if (!last) return;
        fetchPage({ next: { timestamp: last.timestamp, id: last.id } });
    };

    const goPrev = () => {
        const first = items[0];
        if (!first) return;
        fetchPage({ prev: { timestamp: first.timestamp, id: first.id } });
    };

    // Sıralama yönünü değiştir (yeniden eskiye / eskiden yeniye) ve başa dön.
    const changeOrder = (asc) => {
        setAscending(asc);
        fetchPage({ reset: true, ascendingOverride: asc });
    };

    const getExactCount = async () => {
        setCountLoading(true);
        const response = await ApiService.SystemLog.GetSystemLogCount(buildFilter());
        setCountLoading(false);
        if (response.success) {
            setExactCount(response.data);
            message.success(`${t('Exact count')}: ${response.data.toLocaleString()}`);
        }
    };

    useEffect(() => {
        goStart();
        setExactCount(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);

    // Endpointler sekmesinden bir endpoint'e tıklanınca buraya ?route=... ile gelinir: o route'un
    // ham loglarını göster. İlk sorgu doğrudan filtreli çalışsın diye override ile geçilir.
    useEffect(() => {
        const urlRoute = new URLSearchParams(window.location.search).get('route');
        if (!urlRoute)
            return;

        setRoute(urlRoute);
        fetchPage({ reset: true, filterOverride: { route: urlRoute } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Kaynak / kanal / route dropdown'larını lookup tablosundaki gerçek değerlerden doldur.
    useEffect(() => {
        (async () => {
            const s = await ApiService.SystemLog.GetSystemLogSources();
            if (s.success) setSourceOptions(s.data ?? []);
            const c = await ApiService.SystemLog.GetSystemLogChannels();
            if (c.success) setChannelOptions(c.data ?? []);
            const r = await ApiService.SystemLog.GetSystemLogRoutes();
            if (r.success) setRouteOptions(r.data ?? []);
        })();
    }, []);

    // Satırdaki bir kaynağa/kanala tıklayınca ona göre filtreleyip en yeni sayfaya dön.
    // Değer doğrudan fetchPage'e geçilir; state güncellemesi yalnızca kontrolü senkronlar (stale closure önlenir).
    const filterBySource = (value) => {
        setSourceContext(value || '');
        fetchPage({ reset: true, filterOverride: { sourceContext: value || null } });
    };
    const filterByChannel = (value) => {
        setLogSource(value || undefined);
        fetchPage({ reset: true, filterOverride: { logSource: value || null } });
    };
    // Bir isteğin korelasyon kimliğine tıklayınca o isteğin tüm logları (girdiden çıktıya akış) gelir.
    const filterByTraceId = (value) => {
        setTraceId(value || '');
        fetchPage({ reset: true, filterOverride: { traceId: value || null } });
    };
    const filterByActorType = (value) => {
        setActorType(value || undefined);
        fetchPage({ reset: true, filterOverride: { actorType: value || null } });
    };
    const filterByActor = (value) => {
        setActorName(value || '');
        fetchPage({ reset: true, filterOverride: { actorName: value || null } });
    };
    // Bir IP'ye tıklayınca o IP'nin tüm etkinliğini (aktör tipinden bağımsız) göster.
    const filterByIp = (value) => {
        setIp(value || '');
        fetchPage({ reset: true, filterOverride: { ip: value || null } });
    };
    // HTTP path filtresi route ŞABLONU üzerinden çalışır (ham path id taşıdığı için değil).
    const filterByRoute = (value) => {
        setRoute(value || undefined);
        fetchPage({ reset: true, filterOverride: { route: value || null } });
    };
    const filterByStatus = (value) => {
        setStatus(value || undefined);
        fetchPage({ reset: true, filterOverride: statusFilterOf(value) });
    };

    // Tarih aralığını uygula (state + anında fetch; stale closure'ı override ile önle).
    const applyDateRange = (range) => {
        setDateRange(range);
        fetchPage({
            reset: true, filterOverride: {
                dateFrom: range?.[0]?.toISOString() ?? null,
                dateTo: range?.[1]?.toISOString() ?? null,
            }
        });
    };

    // Hızlı seçim: son N takvim günü (bugün dahil). N=1 → sadece bugün.
    const presetLastDays = (n) =>
        applyDateRange([moment().subtract(n - 1, 'days').startOf('day'), moment().endOf('day')]);

    // Dün: sadece dünkü tam gün.
    const presetYesterday = () => {
        const d = moment().subtract(1, 'days');
        applyDateRange([d.clone().startOf('day'), d.clone().endOf('day')]);
    };

    // İleri/geri: mevcut aralığı kendi uzunluğu (gün) kadar kaydır. Aralık yoksa bugüne düş.
    const shiftDateRange = (dir) => {
        if (!dateRange?.[0] || !dateRange?.[1]) {
            presetLastDays(1);
            return;
        }
        const days = Math.max(1, dateRange[1].diff(dateRange[0], 'days') + 1);
        applyDateRange([
            dateRange[0].clone().add(dir * days, 'days'),
            dateRange[1].clone().add(dir * days, 'days'),
        ]);
    };

    const toggleExpanded = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const levelOptions = Object.entries(LEVELS).map(([value, meta]) => ({
        value,
        label: <span style={{ color: meta.color, fontWeight: 600 }}>{meta.code}</span>,
    }));

    return (
        <>
            <Space wrap style={{ marginBottom: 8 }}>
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 140 }}
                    placeholder={t('Level')}
                    options={levelOptions}
                    value={levels}
                    onChange={setLevels}
                    maxTagCount={3}
                />
                <Input
                    allowClear
                    style={{ width: 220 }}
                    placeholder={t('Search in message')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onPressEnter={goStart}
                />
                <Select
                    allowClear
                    showSearch
                    style={{ minWidth: 200 }}
                    placeholder={t('Source')}
                    value={sourceContext || undefined}
                    onChange={filterBySource}
                    options={sourceOptions.map(s => ({ value: s, label: shortSource(s), title: s }))}
                    filterOption={(input, option) =>
                        matchesSearch(`${option.value} ${option.label}`, input)}
                    notFoundContent={t('No logs found')}
                />
                <Select
                    allowClear
                    showSearch
                    style={{ minWidth: 150 }}
                    placeholder={t('Channel')}
                    value={logSource}
                    onChange={filterByChannel}
                    options={(channelOptions.length ? channelOptions : CHANNELS).map(c => ({ value: c, label: c }))}
                />
                <Select
                    allowClear
                    showSearch
                    style={{ minWidth: 240 }}
                    placeholder={t('HTTP Path')}
                    value={route}
                    onChange={filterByRoute}
                    options={routeOptions.map(r => ({ value: r, label: `/${r}`, title: r }))}
                    filterOption={(input, option) => matchesSearch(option.value, input)}
                    notFoundContent={t('No logs found')}
                />
                <Select
                    allowClear
                    style={{ minWidth: 120 }}
                    placeholder={t('Status')}
                    value={status}
                    onChange={filterByStatus}
                    options={STATUS_FILTERS.map(s => ({ value: s.value, label: s.label }))}
                />
                <Select
                    allowClear
                    style={{ minWidth: 130 }}
                    placeholder={t('Actor Type')}
                    value={actorType}
                    onChange={filterByActorType}
                    options={Object.keys(ACTOR_TYPES).map(v => ({ value: v, label: v }))}
                />
                <Input
                    allowClear
                    style={{ width: 150 }}
                    placeholder={t('Actor')}
                    value={actorName}
                    onChange={e => setActorName(e.target.value)}
                    onPressEnter={goStart}
                />
                <Input
                    allowClear
                    style={{ width: 140 }}
                    placeholder={t('IP')}
                    value={ip}
                    onChange={e => setIp(e.target.value)}
                    onPressEnter={goStart}
                />
                <Input
                    allowClear
                    style={{ width: 170 }}
                    placeholder={t('Trace Id')}
                    value={traceId}
                    onChange={e => { const v = e.target.value; setTraceId(v); if (!v) filterByTraceId(''); }}
                    onPressEnter={() => filterByTraceId(traceId)}
                />
                <DatePicker.RangePicker
                    showTime
                    value={dateRange}
                    onChange={applyDateRange}
                />
                <Space.Compact>
                    <Button icon={<LeftOutlined />} title={t('Previous period')} onClick={() => shiftDateRange(-1)} />
                    <Button onClick={() => presetLastDays(1)}>{t('Today')}</Button>
                    <Button onClick={presetYesterday}>{t('Yesterday')}</Button>
                    <Button onClick={() => presetLastDays(3)}>{t('Last 3 days')}</Button>
                    <Button onClick={() => presetLastDays(7)}>{t('Last 7 days')}</Button>
                    <Button onClick={() => presetLastDays(30)}>{t('Last 30 days')}</Button>
                    <Button icon={<RightOutlined />} title={t('Next period')} onClick={() => shiftDateRange(1)} />
                </Space.Compact>
                <Button type="primary" icon={<ReloadOutlined />} onClick={goStart} loading={loading}>
                    {t('Search')}
                </Button>
                <Button icon={<NumberOutlined />} onClick={getExactCount} loading={countLoading}>
                    {t('Get exact count')}
                </Button>
                {exactCount !== null && (
                    <span style={{ fontWeight: 'bold' }}>{exactCount.toLocaleString()}</span>
                )}
            </Space>

            <Space style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                    <Button
                        icon={<VerticalAlignTopOutlined />}
                        onClick={goStart}
                        disabled={loading}
                    >
                        {t('Start')}
                    </Button>
                    <Button
                        icon={<LeftOutlined />}
                        onClick={goPrev}
                        disabled={loading || !hasPrev}
                    >
                        {t('Prev')}
                    </Button>
                    <span style={{ minWidth: 70, textAlign: 'center', display: 'inline-block' }}>
                        {t('Page')}: {pageIndex}
                    </span>
                    <Button
                        onClick={goNext}
                        disabled={loading || !hasNext}
                    >
                        {t('Next')} <RightOutlined />
                    </Button>
                </Space>
                <Space>
                    <Select
                        style={{ width: 170 }}
                        value={ascending}
                        onChange={changeOrder}
                        options={[
                            { value: false, label: t('Newest first') },
                            { value: true, label: t('Oldest first') },
                        ]}
                    />
                    <Select
                        style={{ width: 110 }}
                        value={pageSize}
                        onChange={setPageSize}
                        options={[50, 100, 200, 500].map(v => ({ value: v, label: `${v} / ${t('page')}` }))}
                    />
                </Space>
            </Space>

            <Spin spinning={loading}>
                <div className="system-log-viewer">
                    {items.length === 0 && (
                        <div className="system-log-empty">
                            <Empty description={t('No logs found')} />
                        </div>
                    )}
                    {items.map(item => {
                        const meta = LEVELS[item.level] ?? { code: item.level?.substring(0, 3)?.toUpperCase(), color: '#000' };
                        const expanded = expandedIds.has(item.id);
                        return (
                            <div key={item.id}>
                                <div
                                    className={`system-log-row${expanded ? ' expanded' : ''}`}
                                    onClick={() => toggleExpanded(item.id)}
                                >
                                    <span className="system-log-time">{formatTime(item.timestamp)}</span>
                                    <span className="system-log-level" style={{ color: meta.color }}>{meta.code}</span>
                                    <span
                                        className="system-log-user system-log-clickable"
                                        title={`${item.actorType ?? ''} ${item.actorName ?? ''}\n${t('Filter by this actor')}`}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (item.actorName) filterByActor(item.actorName);
                                            else if (item.actorType) filterByActorType(item.actorType);
                                        }}
                                    >
                                        {item.actorType && (
                                            <span
                                                className="system-log-actor"
                                                style={{ color: (ACTOR_TYPES[item.actorType]?.color) ?? '#8c8c8c' }}
                                            >
                                                {ACTOR_TYPES[item.actorType]?.code ?? item.actorType}
                                            </span>
                                        )}
                                        {item.actorName ?? '-'}
                                    </span>
                                    <span
                                        className="system-log-source system-log-clickable"
                                        title={`${item.sourceContext ?? ''}\n${t('Filter by this source')}`}
                                        onClick={e => { e.stopPropagation(); if (item.sourceContext) filterBySource(item.sourceContext); }}
                                    >
                                        {shortSource(item.sourceContext)}
                                    </span>
                                    <span
                                        className="system-log-trace system-log-clickable"
                                        title={item.traceId ? `${item.traceId}\n${t('Trace this request')}` : ''}
                                        onClick={e => { e.stopPropagation(); if (item.traceId) filterByTraceId(item.traceId); }}
                                    >
                                        {item.traceId ? item.traceId.substring(0, 8) : ''}
                                    </span>
                                    <span className="system-log-message" title={item.message}>
                                        {item.logSource && (
                                            <span
                                                className="system-log-channel system-log-clickable"
                                                title={t('Filter by this channel')}
                                                onClick={e => { e.stopPropagation(); filterByChannel(item.logSource); }}
                                            >
                                                {item.logSource}
                                            </span>
                                        )}
                                        {item.message}
                                    </span>
                                </div>
                                {expanded && (
                                    <div className="system-log-detail" onClick={e => e.stopPropagation()}>
                                        <div>
                                            <span className="system-log-detail-label">{t('Source')}:</span>
                                            {item.sourceContext ?? '-'}
                                            {item.logSource && (
                                                <>
                                                    <span className="system-log-detail-label" style={{ marginLeft: 16 }}>{t('Channel')}:</span>
                                                    <span className="system-log-channel">{item.logSource}</span>
                                                </>
                                            )}
                                            <span className="system-log-detail-label" style={{ marginLeft: 16 }}>{t('Actor')}:</span>
                                            {item.actorType && (
                                                <span
                                                    className="system-log-actor"
                                                    style={{ color: (ACTOR_TYPES[item.actorType]?.color) ?? '#8c8c8c' }}
                                                >
                                                    {item.actorType}
                                                </span>
                                            )}
                                            {item.actorName ?? '-'}
                                            {item.actorId ? ` (${item.actorId})` : ''}
                                            {item.ip && (
                                                <>
                                                    <span className="system-log-detail-label" style={{ marginLeft: 16 }}>{t('IP')}:</span>
                                                    <span
                                                        className="system-log-clickable"
                                                        style={{ color: '#ad6800' }}
                                                        title={t('Filter by this IP')}
                                                        onClick={() => filterByIp(item.ip)}
                                                    >
                                                        {item.ip}
                                                    </span>
                                                </>
                                            )}
                                            {item.traceId && (
                                                <>
                                                    <span className="system-log-detail-label" style={{ marginLeft: 16 }}>{t('Trace Id')}:</span>
                                                    <span
                                                        className="system-log-clickable"
                                                        style={{ color: '#1890ff' }}
                                                        title={t('Trace this request')}
                                                        onClick={() => filterByTraceId(item.traceId)}
                                                    >
                                                        {item.traceId}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {item.requestPath && (
                                            <div style={{ marginTop: 4 }}>
                                                <span className="system-log-detail-label">{t('HTTP Path')}:</span>
                                                {/* Tıklama ham path'i değil, route şablonunu filtreler: aynı endpoint'in tüm çağrıları gelsin. */}
                                                <span
                                                    className={item.route ? 'system-log-clickable' : undefined}
                                                    style={{ color: '#08979c' }}
                                                    title={item.route ? `${item.route}\n${t('Filter by this path')}` : undefined}
                                                    onClick={() => item.route && filterByRoute(item.route)}
                                                >
                                                    {item.httpMethod ? `${item.httpMethod} ` : ''}{item.requestPath}
                                                </span>
                                                {item.statusCode != null && (
                                                    <>
                                                        <span className="system-log-detail-label" style={{ marginLeft: 16 }}>{t('Status')}:</span>
                                                        {item.statusCode}
                                                    </>
                                                )}
                                                {item.elapsedMs != null && (
                                                    <>
                                                        <span className="system-log-detail-label" style={{ marginLeft: 16 }}>{t('Duration (ms)')}:</span>
                                                        {item.elapsedMs}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        <div style={{ marginTop: 4 }}>
                                            <span className="system-log-detail-label">{t('Message')}:</span>
                                        </div>
                                        <pre>{item.message}</pre>
                                        {item.requestBody && (
                                            <>
                                                <span className="system-log-detail-label">{t('Request Body')}:</span>
                                                <pre>{prettyJson(item.requestBody)}</pre>
                                            </>
                                        )}
                                        {item.responseBody && (
                                            <>
                                                <span className="system-log-detail-label">{t('Response Body')}:</span>
                                                <pre>{prettyJson(item.responseBody)}</pre>
                                            </>
                                        )}
                                        {item.properties && (
                                            <>
                                                <span className="system-log-detail-label">{t('Properties')}:</span>
                                                <pre>{prettyJson(item.properties)}</pre>
                                            </>
                                        )}
                                        {item.exception && (
                                            <>
                                                <span className="system-log-detail-label" style={{ color: '#f5222d' }}>{t('Exception')}:</span>
                                                <pre style={{ color: '#a8071a' }}>{item.exception}</pre>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Spin>
        </>
    );
}
